import type {Component, Editor} from 'grapesjs';
import {FORM_MAKER_DESELECT_COMMAND, FORM_MAKER_SAVE_COMMAND, FORM_MAKER_SELECT_NEXT_COMMAND, FORM_MAKER_SELECT_PREV_COMMAND} from './form-maker-commands';
import {syncAllSubmitButtonContainerBackgrounds, syncSubmitButtonContainerBackground} from '@FormMakerReact/lib/form-maker-style-library';
import {POSTERMYWALL_FORM_BACKGROUND_TYPE} from '@FormMakerReact/components/postermywall-form-background/postermywall-form-background';
import {ICON_VECTOR_BG_FILL_PROP, ICON_VECTOR_TICK_FILL_PROP, POSTERMYWALL_ICON_VECTOR_TYPE} from '@FormMakerReact/components/postermywall-icon-vector/postermywall-icon-vector';
import {syncIconVectorContent} from '@FormMakerReact/lib/form-maker-icon-vector-library';
import {seedDefaultLayout} from '@FormMakerReact/lib/form-maker-default-layout';
import {initTextCharCounter} from './text-char-counter/text-char-counter';
import {POSTERMYWALL_DESCRIPTION_TEXT_TYPE} from '@FormMakerReact/components/postermywall-description-text/postermywall-description-text';
import {initDescriptionRtePositioner} from './description-rte/description-rte-positioner';

const DESCRIPTION_MAX_CHARS = 600;

const FORM_BG_COLOR_LISTENER_FLAG = 'pmwSubmitContainerColorListenerBound';
const ICON_VECTOR_LISTENER_FLAG = 'pmwIconVectorColorListenerBound';

export function initListeners(editor: Editor): void {
  editor.onReady(() => {
    disableWrapperInteractivity(editor);
    ensureCanvasBodyClass(editor);
  });

  initTextCharCounter(editor, {
    componentType: POSTERMYWALL_DESCRIPTION_TEXT_TYPE,
    maxChars: DESCRIPTION_MAX_CHARS,
    counterClass: 'pmw-form-description-char-counter',
    counterAtMaxClass: 'pmw-form-description-char-counter-at-max',
    counterVisibleClass: 'pmw-form-description-char-counter-visible',
    listenersBoundFlag: '__pmwDescriptionCharCounterListenersBound',
    charCountText: 'pmwjs_form_maker_description_char_count',
  });
  initDescriptionRtePositioner(editor);

  editor.on('storage:end:load', () => {
    seedDefaultLayout(editor);
    syncAllSubmitButtonContainerBackgrounds(editor);
    bindFormBackgroundColorListeners(editor);
    bindIconVectorColorListeners(editor);
  });

  editor.Keymaps.add('core:save', '⌘+s, ctrl+s', FORM_MAKER_SAVE_COMMAND, {
    prevent: true,
  });
  editor.Keymaps.add('core:undo', '⌘+z, ctrl+z', 'core:undo');
  editor.Keymaps.add('core:redo', '⌘+shift+z, ctrl+shift+z', 'core:redo');
  editor.Keymaps.add('form-maker:deselect', 'escape', FORM_MAKER_DESELECT_COMMAND);
  editor.Keymaps.add('form-maker:select-next', 'tab', FORM_MAKER_SELECT_NEXT_COMMAND, {prevent: true});
  editor.Keymaps.add('form-maker:select-prev', 'shift+tab', FORM_MAKER_SELECT_PREV_COMMAND, {prevent: true});
}

function bindIconVectorColorListeners(editor: Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  wrapper.findType(POSTERMYWALL_ICON_VECTOR_TYPE).forEach((iconVector: Component): void => {
    if (iconVector.get(ICON_VECTOR_LISTENER_FLAG) === true) {
      return;
    }
    iconVector.set(ICON_VECTOR_LISTENER_FLAG, true);
    const sync = (): void => {
      syncIconVectorContent(iconVector);
    };
    iconVector.on(`change:style:${ICON_VECTOR_BG_FILL_PROP}`, sync);
    iconVector.on(`change:style:${ICON_VECTOR_TICK_FILL_PROP}`, sync);
  });
}

function bindFormBackgroundColorListeners(editor: Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  const formBackgrounds = wrapper.findType(POSTERMYWALL_FORM_BACKGROUND_TYPE);
  formBackgrounds.forEach((formBackground: Component): void => {
    if (formBackground.get(FORM_BG_COLOR_LISTENER_FLAG) === true) {
      return;
    }
    formBackground.set(FORM_BG_COLOR_LISTENER_FLAG, true);
    formBackground.on('change:style:background-color', (): void => {
      syncSubmitButtonContainerBackground(formBackground);
    });
  });
}

function ensureCanvasBodyClass(editor: Editor): void {
  editor.Canvas.getBody()?.classList.add('gjs-dashed');
}

function disableWrapperInteractivity(editor: Editor): void {
  editor.getWrapper()?.set({
    selectable: false,
    hoverable: false,
    highlightable: false,
    badgable: false,
  });
}
